import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import styles from './AudioAlertProvider.module.css';
import { Alert } from '../../../utils/alert';
import { useIsMobile } from '../../../utils/hooks';
import * as Sentry from '@sentry/react';

const MAX_PROMPTS = 3;
const PROBE_INTERVAL_MS = 5000;
const PROMPT_STORAGE_KEY = 'audioAlert.promptCount';

type Status = 'unknown' | 'unlocked' | 'blocked' | 'unsupported';

const Ctx = createContext<{
    status: Status;
    unlock: () => Promise<boolean>;
}>({ status: 'unknown', unlock: async () => false });

export const useAudioAlert = () => useContext(Ctx);

export const AudioAlertProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const isMobile = useIsMobile();
    const [status, setStatus] = useState<Status>('unknown');
    const [showPrompt, setShowPrompt] = useState(false);
    const promptCountRef = useRef<number>(
        Number(sessionStorage.getItem(PROMPT_STORAGE_KEY) ?? 0)
    );

    const bumpPromptCount = () => {
        promptCountRef.current += 1;
        sessionStorage.setItem(PROMPT_STORAGE_KEY, String(promptCountRef.current));
    };

    const checkAndMaybePrompt = useCallback(async () => {
        const ok = await Alert.probe();
        if (ok) {
            setStatus('unlocked');
            setShowPrompt(false);
            return;
        }
        // Blocked — show prompt regardless of platform.
        if (promptCountRef.current >= MAX_PROMPTS) {
            setStatus('unsupported');
            setShowPrompt(false);
            Sentry.captureMessage('Audio alerts giving up after MAX_PROMPTS', {
                level: 'warning',
                tags: { feature: 'audio-alert' },
            });
            return;
        }
        setStatus('blocked');
        setShowPrompt(true);
    }, [isMobile]);

    useEffect(() => {
        Alert.init();
        checkAndMaybePrompt();

        // Refresh unlock on every gesture (no { once: true })
        const refresh = () => {
            void Alert.unlock().then(unlockOk => {
                if (!unlockOk) return;
                // unlock() only checks ctx.state — verify the full pipeline
                // (howl loaded + ctx running) before hiding the prompt.
                void Alert.probe().then(probeOk => {
                    if (probeOk) {
                        setStatus('unlocked');
                        setShowPrompt(false);
                    }
                });
            });
        };
        const events: (keyof DocumentEventMap)[] =
            ['click', 'keydown', 'touchstart', 'pointerdown'];
        events.forEach(e => document.addEventListener(e, refresh, { passive: true }));

        const onVisibility = () => {
            if (document.visibilityState === 'visible') checkAndMaybePrompt();
        };
        document.addEventListener('visibilitychange', onVisibility);

        const id = window.setInterval(checkAndMaybePrompt, PROBE_INTERVAL_MS);

        return () => {
            events.forEach(e => document.removeEventListener(e, refresh));
            document.removeEventListener('visibilitychange', onVisibility);
            window.clearInterval(id);
        };
    }, [checkAndMaybePrompt]);

    const unlock = async () => {
        bumpPromptCount();
        const ok = await Alert.unlock();
        if (ok) {
            setStatus('unlocked');
            setShowPrompt(false);
        } else if (promptCountRef.current >= MAX_PROMPTS) {
            setStatus('unsupported');
            setShowPrompt(false);
            Sentry.captureMessage(
                'Audio alerts unsupported: unlock failed after user gesture',
                { level: 'warning', tags: { feature: 'audio-alert' } }
            );
        }
        return ok;
    };

    return (
        <Ctx.Provider value={{ status, unlock }}>
            {children}

            {showPrompt && isMobile && (
                <div className={styles.overlay} onClick={unlock}>
                    <div className={styles.card}>
                        <h3 className={styles.title}>Enable Alerts</h3>
                        <p className={styles.description}>
                            Your browser requires this to enable audio alerts. Tap anywhere to continue.
                        </p>
                    </div>
                </div>
            )}
        </Ctx.Provider>
    );
};
